import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { ESITI_COLLEGAMENTO, ESTENSIONI_FERME_GMAIL, LIMITI_POSTA, type CartaMail, type EsitoCollegamento, type StatoPosta } from '@masamune/shared';
import { api, apiFetch } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { oraDiRoma } from './registroApi';

/**
 * La posta Google di MUNE nel web (Fase 2H, 29/10/2026; os-planning/ai/fase-2h-mail.md): la riga
 * «La tua posta Google» in Impostazioni (collega, scollega, ricollega), il ritorno da Google
 * (`?posta=` nell'indirizzo delle Impostazioni) e i tre tasti della carta «Mail da mandare»
 * (CartaMail.tsx). Le parole a schermo stanno qui.
 */

export const statoPostaQuery = {
  queryKey: ['ai', 'posta'] as const,
  queryFn: () => api.get<StatoPosta>('/ai/posta'),
};

/** Il browser va alla pagina di Google: torna alle Impostazioni con `?posta=…`. */
export async function collegaPosta(): Promise<void> {
  const { url } = await api.post<{ url: string }>('/ai/posta/collega');
  window.location.assign(url);
}

export function scollegaPosta(): Promise<void> {
  return api.delete<void>('/ai/posta');
}

/**
 * Quello che chi manda ha cambiato nella carta (30/10/2026): testo e oggetto solo se diversi da
 * quelli di MUNE, e i file aggiunti. Senza file il corpo è un JSON; coi file un multipart, coi
 * campi `testo` e `oggetto` e i file `allegati`.
 */
export interface CorrezioniMail { testo?: string; oggetto?: string; file?: File[] }

function clic(id: string, quale: 'manda' | 'bozza', c: CorrezioniMail = {}): Promise<CartaMail> {
  const percorso = `/ai/posta/mail/${encodeURIComponent(id)}/${quale}`;
  if (c.file?.length) {
    const corpo = new FormData();
    if (c.testo !== undefined) corpo.append('testo', c.testo);
    if (c.oggetto !== undefined) corpo.append('oggetto', c.oggetto);
    for (const f of c.file) corpo.append('allegati', f, f.name);
    return apiFetch<CartaMail>(percorso, { method: 'POST', body: corpo });
  }
  const json = { ...(c.testo !== undefined ? { testo: c.testo } : {}), ...(c.oggetto !== undefined ? { oggetto: c.oggetto } : {}) };
  return api.post<CartaMail>(percorso, Object.keys(json).length ? json : undefined);
}

export const mandaMail = (id: string, c?: CorrezioniMail) => clic(id, 'manda', c);
export const bozzaMail = (id: string, c?: CorrezioniMail) => clic(id, 'bozza', c);
export const lasciaMail = (id: string) => api.post<CartaMail>(`/ai/posta/mail/${encodeURIComponent(id)}/rifiuta`);

const MB = 1024 * 1024;

/** Il peso come si legge: «850 KB», «2,4 MB». */
export function pesoFile(byte: number): string {
  if (byte < MB) return `${Math.max(1, Math.round(byte / 1024))} KB`;
  return `${(byte / MB).toLocaleString('it-IT', { maximumFractionDigits: 1 })} MB`;
}

/**
 * I file scelti che si aggiungono a quelli già nella carta, con le stesse regole del server: niente
 * programmi e script, al massimo LIMITI_POSTA.allegati file e LIMITI_POSTA.mbAllegati MB insieme.
 * Lo stesso file (nome e peso) scelto due volte resta uno. `fuori`: la frase per quelli lasciati fuori.
 */
export function aggiungiFile(gia: File[], scelti: File[]): { file: File[]; fuori: string | null } {
  const file = [...gia];
  let fuori: string | null = null;
  let peso = gia.reduce((s, f) => s + f.size, 0);
  for (const f of scelti) {
    if (file.some((g) => g.name === f.name && g.size === f.size)) continue;
    const est = f.name.includes('.') ? f.name.slice(f.name.lastIndexOf('.') + 1).toLowerCase() : '';
    if ((ESTENSIONI_FERME_GMAIL as readonly string[]).includes(est)) {
      fuori = PAROLE_POSTA.allegatoFermo(f.name);
      continue;
    }
    if (file.length >= LIMITI_POSTA.allegati) {
      fuori = PAROLE_POSTA.troppiAllegati;
      break;
    }
    if (peso + f.size > LIMITI_POSTA.mbAllegati * MB) {
      fuori = PAROLE_POSTA.allegatiPesanti(f.name);
      continue;
    }
    file.push(f);
    peso += f.size;
  }
  return { file, fuori };
}

/**
 * Le carte delle mail decise in questa sessione, per id: la parte della chat resta quella arrivata
 * col flusso, e la carta mostra l'ultima forma avuta dal server.
 */
export const useCarteMail = create<{ carte: Record<string, CartaMail>; metti: (c: CartaMail) => void }>((set) => ({
  carte: {},
  metti: (c) => set((s) => ({ carte: { ...s.carte, [c.id]: c } })),
}));

export const PAROLE_POSTA = {
  titolo: {
    PROPOSTA: 'Mail da mandare',
    MANDATA: 'Mail mandata',
    BOZZA: 'Mail salvata tra le bozze',
    LASCIATA: 'Mail lasciata stare',
    SCADUTA: 'Mail scaduta',
    FORSE: 'Mail forse partita',
  } satisfies Record<CartaMail['stato'], string>,
  nonSiRitira: 'Una mail mandata non si ritira.',
  correggi: 'Puoi correggere oggetto e testo e allegare file prima di mandarla.',
  testoVuoto: 'Il testo della mail è vuoto.',
  oggettoVuoto: 'L’oggetto della mail è vuoto.',
  etichettaTesto: 'Testo della mail, si può correggere',
  etichettaOggetto: 'Oggetto della mail, si può correggere',
  allega: 'Allega file',
  togli: (nome: string) => `Togli ${nome}`,
  allegatoFermo: (nome: string) => `Gmail non manda programmi e script: «${nome}» resta fuori.`,
  troppiAllegati: `Al massimo ${LIMITI_POSTA.allegati} allegati per mail.`,
  allegatiPesanti: (nome: string) => `Gli allegati insieme possono pesare al massimo ${LIMITI_POSTA.mbAllegati} MB: «${nome}» resta fuori.`,
  manda: 'Manda',
  mando: 'Mando…',
  bozza: 'Salva in Gmail',
  salvo: 'Salvo…',
  lascia: 'Lascia stare',
  apri: 'Apri in Gmail',
  tutto: 'Mostra tutto',
  meno: 'Mostra meno',
  campi: { da: 'Da', a: 'A', cc: 'Cc', oggetto: 'Oggetto', allegati: 'Allegati' },
  // Impostazioni.
  riga: 'La tua posta Google',
  spiegaSpenta: 'Non ancora attiva: sul server manca il collegamento con Google.',
  spiegaNonCollegata: 'Se la colleghi, MUNE cerca e legge le tue mail quando glielo chiedi e prepara le risposte. Una mail parte solo quando premi Manda.',
  spiegaCollegata: (email: string) => `Collegata: ${email}. MUNE legge e prepara le mail, partono solo col tuo clic.`,
  spiegaDaRicollegare: (email: string) => `Google non accetta più il permesso di ${email}: ricollegala.`,
  collega: 'Collega la posta',
  collego: 'Apro Google…',
  ricollega: 'Ricollega',
  scollega: 'Scollega',
  scollego: 'Scollego…',
  confermaScollega: 'MUNE non potrà più leggere né preparare le tue mail. Le mail già mandate restano.',
  scollegata: 'Posta scollegata',
  ritorno: {
    collegata: 'Posta collegata: adesso MUNE può leggere e preparare le tue mail.',
    negata: 'Non hai dato il permesso su Google: la posta resta scollegata.',
    'altra-casella': 'Hai scelto un’altra casella: collega quella con cui entri nel gestionale.',
    scaduto: 'Il collegamento è scaduto: riprova.',
    permessi: 'Su Google mancava un permesso: riprova e lascia spuntate tutte le voci.',
    errore: 'Il collegamento con Google non è riuscito: riprova tra poco.',
  } satisfies Record<EsitoCollegamento, string>,
} as const;

/** La riga sotto il titolo della carta. */
export function fraseCartaMail(c: CartaMail): string {
  switch (c.stato) {
    case 'PROPOSTA':
      return `Vale fino alle ${oraDiRoma(c.scade)}`;
    case 'MANDATA':
      return c.il ? `Partita alle ${oraDiRoma(c.il)}` : 'Partita';
    case 'BOZZA':
      return 'Non è partita: la trovi tra le bozze di Gmail';
    case 'LASCIATA':
      return 'Non è partita';
    case 'SCADUTA':
      return 'Non è partita: chiedimela di nuovo';
    case 'FORSE':
      return 'Gmail non ha risposto: controlla tra le inviate prima di rimandarla';
  }
}

/**
 * Il ritorno da Google: l'avviso con l'esito, la casella riletta, e `posta` tolto dall'indirizzo
 * (un ricaricamento non ripete l'avviso). Lo usano la scheda MUNE al computer e al telefono.
 */
export function useRitornoPosta(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const u = new URL(window.location.href);
    const esito = u.searchParams.get('posta');
    if (!esito || !(ESITI_COLLEGAMENTO as readonly string[]).includes(esito)) return;
    const e = esito as EsitoCollegamento;
    if (e === 'collegata') toast.ok(PAROLE_POSTA.ritorno[e]);
    else toast.alert(PAROLE_POSTA.ritorno[e]);
    void qc.invalidateQueries({ queryKey: statoPostaQuery.queryKey });
    u.searchParams.delete('posta');
    window.history.replaceState(window.history.state, '', `${u.pathname}${u.search}${u.hash}`);
  }, [qc]);
}
